import React, { useEffect, useMemo, useState } from 'react'
import { FiSettings } from 'react-icons/fi'
import MeunSideSetting from '../components/settings/MeunSideSetting'
import { AlertMsg, MwButton, MwInputFile, MwInputText } from '../components/ui'
import { useCurrent, usePost, usePut } from '../hooks'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { setisLogging } from '../store/authSlice'
import { useLocation } from 'react-router-dom'

const Settings = () => {
  const [t] = useTranslation('global')
  const { user, currentLangId } = useCurrent()
  const dispatch = useDispatch()
  const location = useLocation()
  const { loading: savingRequest, putData } = usePut()
  const { data: uploadResult, error: uploadError, postData: uploadFile } = usePost(false)
  const [uploadingPic, setUploadingPic] = useState(false)

  const initialTab = useMemo(() => {
    const requested = location?.state?.tab || new URLSearchParams(location.search).get('tab')
    if (requested === 'password' || requested === 'profile') return requested
    return 'profile'
  }, [location?.state?.tab, location.search])

  const [activeTab, setActiveTab] = useState(initialTab)

  useEffect(() => {
    setActiveTab(initialTab)
  }, [initialTab])

  const isAr = currentLangId === 'ar'

  // --- حالة بيانات الحساب ---
  const [profileName, setProfileName] = useState(user?.name || '')
  const [profileMobile, setProfileMobile] = useState(user?.mobile || '')
  const [profileEmail, setProfileEmail] = useState(user?.email || '')
  const [profileJobTitle, setProfileJobTitle] = useState(user?.jobTitle || '')
  const [profilePic, setProfilePic] = useState(user?.userPic || '')
  const [profileAlert, setProfileAlert] = useState(null)

  useEffect(() => {
    setProfileName(user?.name || '')
    setProfileMobile(user?.mobile || '')
    setProfileEmail(user?.email || '')
    setProfileJobTitle(user?.jobTitle || '')
    setProfilePic(user?.userPic || '')
  }, [user?.email, user?.jobTitle, user?.mobile, user?.name, user?.userPic])

  useEffect(() => {
    if (uploadResult?.data?.location) {
      setProfilePic(uploadResult.data.location)
      setUploadingPic(false)
    }
  }, [uploadResult])

  useEffect(() => {
    if (uploadError) {
      setUploadingPic(false)
      setProfileAlert({ type: 'Error', msg: isAr ? 'فشل رفع الصورة' : 'Failed to upload image' })
    }
  }, [isAr, uploadError])

  const uploadProfilePic = (e) => {
    const file = e?.target?.files?.[0] || null
    if (!file) return
    setProfileAlert(null)
    setUploadingPic(true)
    const formData = new FormData()
    formData.append('myfile', file, file.name)
    uploadFile(`${process.env.REACT_APP_UPLOADS_API_BASE_URL}/users`, formData, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      timeout: 0,
    })
  }

  const submitProfileUpdate = async (e) => {
    e.preventDefault()
    setProfileAlert(null)

    const name = String(profileName || '').trim()
    const mobile = String(profileMobile || '').trim()
    const email = String(profileEmail || '').trim()

    if (!name) {
      setProfileAlert({ type: 'Error', msg: isAr ? 'الاسم مطلوب' : 'Name is required' })
      return
    }
    if (!email) {
      setProfileAlert({ type: 'Error', msg: isAr ? 'البريد الإلكتروني مطلوب' : 'Email is required' })
      return
    }

    try {
      const result = await putData(`${process.env.REACT_APP_Auth_API_BASE_URL}/users/update`, { name, mobile, email, jobTitle: profileJobTitle, userPic: profilePic })
      const updatedUser = result?.data || result?.user || result?.data?.user || null
      
      if (result?.status === 1 || result?.status === true || updatedUser) {
        const payload = updatedUser || { ...user, name, mobile, email, jobTitle: profileJobTitle, userPic: profilePic }
        localStorage.setItem('user', JSON.stringify(payload))
        localStorage.setItem('userCurrentTs', Date.now().toString())
        dispatch(setisLogging({ user: payload, isLogging: true }))
        setProfileAlert({ type: 'Success', msg: isAr ? 'تم تحديث بيانات الحساب بنجاح' : 'Account updated successfully' })
        return
      }
      setProfileAlert({ type: 'Error', msg: result?.message || (isAr ? 'فشل تحديث الحساب' : 'Failed to update account') })
    } catch (err) {
      const apiMsg = err?.response?.data?.message
      setProfileAlert({ type: 'Error', msg: apiMsg || (isAr ? 'فشل تحديث الحساب' : 'Failed to update account') })
    }
  }

  // --- حالة تغيير كلمة المرور ---
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [passwordAlert, setPasswordAlert] = useState(null)

  const submitChangePassword = async (e) => {
    e.preventDefault()
    setPasswordAlert(null)

    const current = String(currentPassword || '').trim()
    const next = String(newPassword || '').trim()
    const confirm = String(confirmNewPassword || '').trim()

    if (!current || !next || !confirm) {
      setPasswordAlert({ type: 'Error', msg: t('support.changePassword.errors.required') })
      return
    }
    if (next !== confirm) {
      setPasswordAlert({ type: 'Error', msg: t('support.changePassword.errors.mismatch') })
      return
    }
    if (next.length < 6) {
      setPasswordAlert({ type: 'Error', msg: t('support.changePassword.errors.tooShort') })
      return
    }

    try {
      const result = await putData(`${process.env.REACT_APP_Auth_API_BASE_URL}/users/change-password`, {
        currentPassword: current,
        newPassword: next,
      })
      if (result?.status === 1) {
        setCurrentPassword('')
        setNewPassword('')
        setConfirmNewPassword('')
        setPasswordAlert({ type: 'Success', msg: t('support.changePassword.success') })
        return
      }
      setPasswordAlert({ type: 'Error', msg: result?.message || t('support.changePassword.errors.failed') })
    } catch (err) {
      const apiMsg = err?.response?.data?.message
      setPasswordAlert({ type: 'Error', msg: apiMsg || t('support.changePassword.errors.failed') })
    }
  }

  return (
    <>
      <div className='flex justify-between items-center pb-5 pt-2 min-w-0'>
        <div className='flex gap-2 items-center text-sm text-slate-600 font-semibold min-w-0'>
          <FiSettings size={20} className="text-indigo-600" />
          <span className="truncate">{t('support.settings.title')}</span>
        </div>
      </div>

      <div className='flex flex-col md:flex-row gap-6 min-w-0'>
        {/* القائمة الجانبية للإعدادات */}
        <div className='w-full md:w-56 shrink-0'>
          <MeunSideSetting
            activeMenu={activeTab === 'profile' ? 'MyAccount' : 'ChangePassword'}
            setActiveMenu={(menu) => {
              if (menu === 'MyAccount') setActiveTab('profile')
              if (menu === 'ChangePassword') setActiveTab('password')
            }}
            isAr={isAr}
          />
        </div>

        {/* منطقة المحتوى */}
        <div className='flex-1 min-w-0 bg-white rounded-lg border border-slate-200 shadow-sm p-6 overflow-hidden'>
          {activeTab === 'profile' ? (
            <div className="max-w-xl min-w-0">
              <h2 className="text-lg font-bold text-slate-800 mb-4 break-words">{isAr ? 'تحديث بيانات الحساب' : 'Update Account Details'}</h2>
              {profileAlert?.msg && (
                <AlertMsg type={profileAlert?.type === 'Error' ? 'Error' : undefined}>
                  {profileAlert.msg}
                </AlertMsg>
              )}
              <form onSubmit={submitProfileUpdate} className='flex flex-col gap-4'>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-16 h-16 rounded-full overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
                    {profilePic ? (
                      <img
                        src={profilePic}
                        alt={profileName || 'profile'}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none' }}
                      />
                    ) : (
                      <span className="text-slate-500 font-bold">{(profileName || '?').charAt(0)}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <MwInputFile
                      id="profilePic"
                      label={isAr ? 'الصورة الشخصية' : 'Profile photo'}
                      acceptTypes="image/*"
                      loading={uploadingPic}
                      onChangeHandler={uploadProfilePic}
                      className="p-0"
                    />
                  </div>
                </div>
                <MwInputText
                  id="profileName"
                  label={isAr ? 'الاسم' : 'Name'}
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  classNameInput="text-start"
                />
                <MwInputText
                  id="profileJobTitle"
                  label={isAr ? 'المسمى الوظيفي' : 'Job title'}
                  value={profileJobTitle}
                  onChange={(e) => setProfileJobTitle(e.target.value)}
                  classNameInput="text-start"
                />
                <MwInputText
                  id="profileMobile"
                  label={isAr ? 'رقم الجوال' : 'Mobile'}
                  value={profileMobile}
                  onChange={(e) => setProfileMobile(e.target.value)}
                  classNameInput="text-start"
                />
                <MwInputText
                  id="profileEmail"
                  label={isAr ? 'البريد الإلكتروني' : 'Email'}
                  value={profileEmail}
                  onChange={(e) => setProfileEmail(e.target.value)}
                  classNameInput="text-start"
                />
                <div className='pt-2 flex items-center gap-2'>
                  <MwButton actionType="submit" type="saveBtn" disabled={savingRequest}>
                    {isAr ? 'حفظ التغييرات' : 'Save Changes'}
                  </MwButton>
                </div>
              </form>
            </div>
          ) : (
            <div className="max-w-xl min-w-0">
              <h2 className="text-lg font-bold text-slate-800 mb-4 break-words">{t('support.changePassword.title')}</h2>
              {passwordAlert?.msg && (
                <AlertMsg type={passwordAlert?.type === 'Error' ? 'Error' : undefined}>
                  {passwordAlert.msg}
                </AlertMsg>
              )}
              <form onSubmit={submitChangePassword} className='flex flex-col gap-4'>
                <MwInputText
                  id="currentPassword"
                  label={t('support.changePassword.currentPassword')}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  inputType="password"
                  classNameInput="text-start"
                />
                <MwInputText
                  id="newPassword"
                  label={t('support.changePassword.newPassword')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  inputType="password"
                  classNameInput="text-start"
                />
                <MwInputText
                  id="confirmNewPassword"
                  label={t('support.changePassword.confirmNewPassword')}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  inputType="password"
                  classNameInput="text-start"
                />
                <div className='pt-2 flex items-center gap-2'>
                  <MwButton actionType="submit" type="saveBtn" disabled={savingRequest}>
                    {t('support.changePassword.save')}
                  </MwButton>
                  <div className='text-xs text-slate-400 min-w-0 break-all'>
                    {user?.username ? `${t('support.changePassword.userLabel')}: ${user.username}` : ''}
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Settings
